import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Check, ChevronDown, ChevronRight, Clock, ExternalLink, Flag, Lightbulb, MessageCircleWarning, ThumbsDown, ThumbsUp, X } from 'lucide-react';
import axiosInstance from '../utils/axiosInstance';
import { timeAgo } from '../components/bible/rich';
import V2Shell from './V2Shell';

const API = 'api/replai/v2/insights/';
const GORGIAS = 'https://lamptwist.gorgias.com/app/ticket/';
const label = (c) => (c || 'other').replace(/_/g, ' ');
const hours = (h) => (h == null ? '–' : h < 1 ? '< 1 h' : h >= 9 ? `${(h / 9).toFixed(1)} days` : `${h} h`);
const MOOD = { calm: '🙂', neutral: '😐', frustrated: '😠', angry: '😡', unknown: '·' };

const Chip = ({ active, onClick, children }) => (
  <button type="button" onClick={onClick}
    className={`rounded-md px-3 py-1.5 text-sm ${active ? 'bg-[var(--ink)] text-white' : 'text-[var(--ink-2)] hover:bg-[var(--line-2)]'}`}>
    {children}
  </button>
);

const Stat = ({ v, l, warn, team }) => (
  <div>
    <div className={`num text-lg font-semibold ${warn ? 'text-red-700' : ''}`}>{v ?? '–'}</div>
    <div className="text-xs text-[var(--ink-3)]">{l}{team != null && <span> · team {team}</span>}</div>
  </div>
);

const AgentCard = ({ a, team, active, onClick }) => (
  <button type="button" onClick={onClick}
    className={`rounded-lg border bg-[var(--surface)] p-4 text-left ${active ? 'border-[var(--ink)]' : 'border-[var(--line)] hover:border-[var(--ink-3)]'}`}>
    <div className="flex items-baseline justify-between gap-2">
      <span className="font-semibold">{a.agent}</span>
      <span className="text-xs text-[var(--ink-3)]">{a.tickets} tickets{a.few ? ' · few, read with care' : ''}</span>
    </div>
    <div className="mt-3 grid grid-cols-3 gap-3">
      <Stat v={hours(a.first_reply_h)} l="first reply" team={hours(team.first_reply_h)} />
      <Stat v={a.missed_pct != null ? `${a.missed_pct}%` : null} l="step missed" team={`${team.missed_pct}%`} />
      <Stat v={a.chased_pct != null ? `${a.chased_pct}%` : null} l="customer chased" team={`${team.chased_pct}%`} />
      <Stat v={a.mood_worse_pct != null ? `${a.mood_worse_pct}%` : null} l="mood got worse" />
      <Stat v={a.unanswered} l="left unanswered" warn={a.unanswered > 0} />
      <Stat v={a.escalations} l="escalations" warn={a.escalations > 0} />
    </div>
    {a.top_missed?.length > 0 && (
      <div className="mt-3 border-t border-[var(--line-2)] pt-2 text-xs text-[var(--ink-2)]">
        <div className="font-semibold text-[var(--ink-3)]">Most often missed</div>
        {a.top_missed.slice(0, 2).map((m) => <div key={m.step} className="truncate">{m.count}× {m.step}</div>)}
      </div>
    )}
  </button>
);

const Row = ({ r, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const save = (agree) => axiosInstance.post(`${API}${r.id}/`, { agree, note }).then(({ data }) => onSaved(data));
  const m = r.metrics || {};
  const flags = [
    r.steps_missed.filter((x) => (x.severity || 'important') === 'important').length > 0 && `${r.steps_missed.filter((x) => (x.severity || 'important') === 'important').length} important step(s) missed`,
    m.unanswered && r.needs_reply !== false && 'unanswered',
    m.chasers > 0 && `chased ${m.chasers}×`,
    r.promises.some((p) => p.kept === false) && 'promise not kept',
    r.factual_issues.length > 0 && 'wrong fact',
    r.escalation && r.escalation,
  ].filter(Boolean);
  return (
    <li className="rounded-lg border border-[var(--line)] bg-[var(--surface)]">
      <button type="button" onClick={() => setOpen(!open)} className="flex w-full items-start gap-3 p-3 text-left">
        {open ? <ChevronDown size={16} className="mt-1 shrink-0" /> : <ChevronRight size={16} className="mt-1 shrink-0" />}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="font-semibold">{r.agent}</span>
            <span className="text-sm text-[var(--ink-3)]">{r.case_type || label(r.category)} · {r.customer} · {timeAgo(r.at)}</span>
            <span className="text-sm">{MOOD[r.mood_start] || '·'}→{MOOD[r.mood_end] || '·'}</span>
          </div>
          <div className="mt-0.5 text-sm text-[var(--ink-2)]">{r.summary}</div>
          <div className="mt-1 flex flex-wrap gap-1.5 text-xs">
            <span className="flex items-center gap-1 rounded-full bg-[var(--paper)] px-2 py-0.5"><Clock size={11} /> first reply {hours(m.first_reply_h)}</span>
            {flags.map((f) => <span key={f} className="rounded-full bg-amber-100 px-2 py-0.5 text-amber-800">{f}</span>)}
            {r.feedback?.by && <span className={`rounded-full px-2 py-0.5 ${r.feedback.agree ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{r.feedback.agree ? 'agreed' : 'corrected'} by {r.feedback.by}</span>}
          </div>
        </div>
      </button>
      {open && (
        <div className="space-y-3 border-t border-[var(--line-2)] p-4 text-sm">
          <a href={`${GORGIAS}${r.ticket}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-medium hover:underline">
            Ticket {r.ticket} <ExternalLink size={12} /></a>
          <span className="ml-2 text-[var(--ink-3)]">{r.subject}{r.other_agents?.length ? ` · also: ${r.other_agents.join(', ')}` : ''}</span>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <div className="mb-1 flex items-center gap-1.5 font-semibold text-green-800"><ThumbsUp size={14} /> Went well</div>
              <ul className="list-disc space-y-0.5 pl-5">{(r.went_well.length ? r.went_well : ['–']).map((x) => <li key={x}>{x}</li>)}</ul>
            </div>
            <div>
              <div className="mb-1 flex items-center gap-1.5 font-semibold text-amber-800"><AlertTriangle size={14} /> Could be better</div>
              <ul className="list-disc space-y-0.5 pl-5">
                {r.steps_missed.map((s) => <li key={s.step}>{s.severity === 'minor' ? <span className="text-[var(--ink-3)]">(minor) </span> : null}{s.step}{s.bible_block ? <span className="text-[var(--ink-3)]"> (Bible {s.bible_block})</span> : ''}</li>)}
                {r.could_improve.map((x) => <li key={x}>{x}</li>)}
                {r.factual_issues.map((x) => <li key={x} className="text-red-800">{x}</li>)}
                {r.promises.filter((p) => p.kept === false).map((p) => <li key={p.promise}>Promise not kept: {p.promise}</li>)}
                {!r.steps_missed.length && !r.could_improve.length && !r.factual_issues.length && <li>–</li>}
              </ul>
            </div>
          </div>
          {r.coaching_tip && <div className="flex gap-2 rounded-md bg-[var(--paper)] p-3"><Lightbulb size={16} className="shrink-0 text-amber-600" />{r.coaching_tip}</div>}
          <div className="text-xs text-[var(--ink-3)]">
            Measured: first reply {hours(m.first_reply_h)} · longest wait {hours(m.longest_wait_h)} · {m.customer_messages} customer / {m.agent_replies} agent messages
            {m.unanswered ? ` · waiting ${hours(m.unanswered_h)} for an answer` : ''} (working hours, Mon-Fri 9-18)
          </div>
          <div className="flex flex-wrap items-center gap-2 border-t border-[var(--line-2)] pt-3">
            <span className="text-[var(--ink-3)]">Is this assessment right?</span>
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="What's wrong or missing (optional)"
              className="min-w-[16rem] flex-1 rounded-md border border-[var(--line)] px-2 py-1.5" />
            <button type="button" onClick={() => save(true)} className="inline-flex items-center gap-1 rounded-md border border-[var(--line)] px-3 py-1.5 hover:bg-[var(--paper)]"><Check size={14} /> Agree</button>
            <button type="button" onClick={() => save(false)} className="inline-flex items-center gap-1 rounded-md border border-[var(--line)] px-3 py-1.5 hover:bg-[var(--paper)]"><X size={14} /> Not right</button>
          </div>
        </div>
      )}
    </li>
  );
};

const Flags = ({ flags, agents, onChange }) => {
  const [ref, setRef] = useState('');
  const [note, setNote] = useState('');
  const [who, setWho] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const add = () => {
    setBusy(true); setErr('');
    axiosInstance.post(`${API}flags/`, { ref, note, agent: who }).then(({ data }) => { onChange([data, ...flags]); setRef(''); setNote(''); })
      .catch((e) => setErr(e?.response?.data?.error || 'Could not flag this ticket.')).finally(() => setBusy(false));
  };
  const update = (f, body) => axiosInstance.post(`${API}flags/${f.id}/`, body)
    .then(({ data }) => onChange(body.delete ? flags.filter((x) => x.id !== f.id) : flags.map((x) => (x.id === f.id ? data : x))));
  const open = flags.filter((f) => !f.done_at);
  return (
    <section className="mt-6 rounded-lg border border-[var(--line)] bg-[var(--surface)] p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-semibold"><Flag size={15} className="mr-1.5 inline" />Flagged for feedback <span className="font-normal text-[var(--ink-3)]">({open.length} open)</span></h2>
        <span className="text-xs text-[var(--ink-3)]">Tickets you want to discuss with an agent. Replai assesses each one when you flag it.</span>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
        <input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="Ticket number or R-number" className="w-48 rounded-md border border-[var(--line)] px-2 py-1.5" />
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="What to give feedback on" className="min-w-[16rem] flex-1 rounded-md border border-[var(--line)] px-2 py-1.5" />
        <select value={who} onChange={(e) => setWho(e.target.value)} className="rounded-md border border-[var(--line)] bg-[var(--surface)] px-2 py-1.5">
          <option value="">Agent: who replied</option>
          {agents.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <button type="button" disabled={busy || !/\d/.test(ref)} onClick={add} className="rounded-md bg-[var(--ink)] px-3 py-1.5 font-medium text-white disabled:opacity-50">{busy ? 'Flagging…' : 'Flag'}</button>
        {err && <span className="text-red-700">{err}</span>}
      </div>
      {flags.length > 0 && (
        <ul className="mt-3 divide-y divide-[var(--line-2)] text-sm">
          {flags.map((f) => (
            <li key={f.id} className={`flex flex-wrap items-center gap-x-3 gap-y-1 py-2 ${f.done_at ? 'opacity-60' : ''}`}>
              <a href={`${GORGIAS}${f.ticket}`} target="_blank" rel="noreferrer" className="font-medium hover:underline">#{f.ticket}</a>
              <span className="font-semibold">{f.agent || '–'}</span>
              <span className="min-w-0 flex-1 truncate text-[var(--ink-2)]">{f.note || f.subject}</span>
              <span className="text-xs text-[var(--ink-3)]">{f.flagged_by} · {timeAgo(f.at)}</span>
              <button type="button" onClick={() => update(f, { done: !f.done_at })} className="rounded-md border border-[var(--line)] px-2 py-1 text-xs hover:bg-[var(--paper)]">{f.done_at ? 'Reopen' : 'Feedback given'}</button>
              <button type="button" onClick={() => update(f, { delete: true })} className="text-xs text-[var(--ink-3)] hover:text-red-700">Remove</button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

const InsightsV2 = () => {
  const [days, setDays] = useState(30);
  const [agent, setAgent] = useState('');
  const [cat, setCat] = useState('');
  const [only, setOnly] = useState('');
  const [d, setD] = useState(null);
  useEffect(() => { axiosInstance.get(API, { params: { days } }).then(({ data }) => setD(data)).catch((e) => setD(e?.response?.status === 403 ? 'denied' : false)); }, [days]);
  const replace = (row) => setD((x) => ({ ...x, rows: x.rows.map((r) => (r.id === row.id ? row : r)) }));
  const rows = useMemo(() => ((d && d !== 'denied' && d.rows) || []).filter((r) => (!agent || r.agent === agent) && (!cat || (r.category || 'other') === cat)
    && (!only || (only === 'issues' ? (r.steps_missed.some((x) => (x.severity || 'important') === 'important') || (r.metrics?.unanswered && r.needs_reply !== false) || r.metrics?.chasers || r.escalation) : only === 'tips' ? r.coaching_tip : true))), [d, agent, cat, only]);

  return (
    <V2Shell>
      <div className="px-6 py-10 sm:px-10 xl:px-14">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-[2.4rem] font-semibold leading-[1.1]">Agent insights</h1>
            <p className="prose-body mt-2 max-w-3xl text-[var(--ink-2)]">How tickets are handled, for coaching and better processes, not as a score. Speed is measured; the rest is Replai's reading of the thread against the Bible. <b>First version:</b> please mark assessments as right or not, so Replai can be calibrated.</p>
          </div>
          <div className="flex gap-1">{[14, 30, 90].map((n) => <Chip key={n} active={days === n} onClick={() => setDays(n)}>{n} days</Chip>)}</div>
        </div>
        {d === false && <p className="mt-8 text-[var(--ink-2)]">Could not load. Reload the page to try again.</p>}
        {d === 'denied' && <p className="mt-8 text-[var(--ink-2)]">Agent insights are only open to the support leads.</p>}
        {d == null && <div className="mt-8 h-64 animate-pulse rounded-lg bg-[var(--line-2)]" />}
        {d && d !== 'denied' && (
          <>
            <p className="mt-6 text-sm text-[var(--ink-2)]">
              Based on <b>{d.rows.length}</b> assessed tickets: the most recent tickets an agent replied to, so the number per agent
              is simply how many of those were theirs, not a fixed sample per person. Compare the rates, not the counts.
            </p>
            <Flags flags={d.flags || []} agents={[...new Set([...d.agents.map((a) => a.agent), ...(d.flags || []).map((f) => f.agent).filter(Boolean)])].sort()}
              onChange={(flags) => setD((x) => ({ ...x, flags }))} />
            <section className="mt-6 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {d.agents.map((a) => <AgentCard key={a.agent} a={a} team={d.team} active={agent === a.agent} onClick={() => setAgent(agent === a.agent ? '' : a.agent)} />)}
            </section>
            <section className="mt-6 overflow-x-auto rounded-lg border border-[var(--line)] bg-[var(--surface)]">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-[var(--ink-3)]"><tr>
                  <th className="px-3 py-2">Case type</th><th className="px-3 py-2">Tickets</th><th className="px-3 py-2">First reply</th>
                  <th className="px-3 py-2">Longest wait</th><th className="px-3 py-2">Step missed</th><th className="px-3 py-2">Chased</th>
                  <th className="px-3 py-2">Most often missed</th></tr></thead>
                <tbody>
                  {d.categories.map((c) => (
                    <tr key={c.category} onClick={() => setCat(cat === c.category ? '' : c.category)}
                      className={`cursor-pointer border-t border-[var(--line-2)] ${cat === c.category ? 'bg-[var(--paper)]' : 'hover:bg-[var(--paper)]'}`}>
                      <td className="px-3 py-2 font-medium first-letter:uppercase">{label(c.category)}</td>
                      <td className="num px-3 py-2">{c.tickets}</td><td className="px-3 py-2">{hours(c.first_reply_h)}</td>
                      <td className="px-3 py-2">{hours(c.longest_wait_h)}</td><td className="px-3 py-2">{c.missed_pct}%</td>
                      <td className="px-3 py-2">{c.chased_pct}%</td>
                      <td className="max-w-md truncate px-3 py-2 text-[var(--ink-2)]">{c.top_missed?.[0] ? `${c.top_missed[0].count}× ${c.top_missed[0].step}` : '–'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
            <div className="mt-6 flex flex-wrap items-center gap-2 border-b border-[var(--line)] pb-3">
              <Chip active={!only} onClick={() => setOnly('')}>All tickets ({rows.length})</Chip>
              <Chip active={only === 'issues'} onClick={() => setOnly('issues')}><MessageCircleWarning size={14} className="mr-1 inline" />With an issue</Chip>
              <Chip active={only === 'tips'} onClick={() => setOnly('tips')}><Lightbulb size={14} className="mr-1 inline" />With a coaching tip</Chip>
              {(agent || cat) && <span className="ml-2 text-sm text-[var(--ink-2)]">{agent && <b>{agent}</b>} {cat && <>· {label(cat)}</>} <button type="button" className="underline" onClick={() => { setAgent(''); setCat(''); }}>clear</button></span>}
            </div>
            {rows.length ? <ul className="mt-4 space-y-2">{rows.map((r) => <Row key={r.id} r={r} onSaved={replace} />)}</ul>
              : <p className="mt-6 text-[var(--ink-2)]">No assessed tickets here yet.</p>}
          </>
        )}
      </div>
    </V2Shell>
  );
};

export default InsightsV2;
